import { Fragment } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { ContentCopyOutlined, TimerOutlined } from "@mui/icons-material";
import CopyToClipboard from "@/components/common/CopyToClipboard";
import { BannerCta } from "./BannerParts";
import {
  BANNER_VARIANTS,
  COUNTDOWN_INSET_SHADOW,
  type BannerIconTone,
  type BannerVariant,
} from "./bannerVariants";

const DAY = 24 * 60 * 60;

type CountdownUnit = { value: number; unit: string; short: string };

/**
 * The time left in hours, minutes and seconds under a day, and in days,
 * hours and minutes from a day up. Never below zero.
 */
const countdownUnits = (seconds: number): CountdownUnit[] => {
  const left = Math.max(0, Math.floor(seconds));
  const days = Math.floor(left / DAY);
  const hours = Math.floor((left % DAY) / 3600);
  const minutes = Math.floor((left % 3600) / 60);
  if (days > 0) {
    return [
      { value: days, unit: "day", short: "d" },
      { value: hours, unit: "hour", short: "h" },
      { value: minutes, unit: "min", short: "m" },
    ];
  }
  return [
    { value: hours, unit: "hour", short: "h" },
    { value: minutes, unit: "min", short: "m" },
    { value: left % 60, unit: "sec", short: "s" },
  ];
};

const twoDigits = (value: number) => String(value).padStart(2, "0");

/** 09:42:17 under a day, 2d 14h 37m from a day up. */
const compactTime = (seconds: number) => {
  const units = countdownUnits(seconds);
  return units[0].unit === "day"
    ? units.map(({ value, short }) => `${value}${short}`).join(" ")
    : units.map(({ value }) => twoDigits(value)).join(":");
};

/** The countdown's colours, following the banner's style as its icon tile does. */
const COUNTDOWN_TONES: Record<
  BannerIconTone,
  {
    background: string;
    icon: string;
    digits: string;
    colon: string;
    label: string;
    inset: boolean;
  }
> = {
  light: {
    background: "primary.selected",
    icon: "primary.main",
    digits: "text.primary",
    colon: "text.secondary",
    label: "text.secondary",
    inset: false,
  },
  color: {
    background: "banner.countdownColorBackground",
    icon: "common.white",
    digits: "common.white",
    colon: "common.white",
    label: "common.white",
    inset: true,
  },
  dark: {
    background: "banner.promoIconBackground",
    icon: "common.white",
    digits: "common.white",
    colon: "common.white",
    label: "banner.darkSecondaryText",
    inset: true,
  },
};

type BannerCountdownProps = {
  seconds: number;
  tone: BannerIconTone;
  /** Compact on the Top bar; boxes on the Sticky bottom bar, Pop-up and Slide-in. */
  look: "compact" | "boxes";
};

export const BannerCountdown = ({
  seconds,
  tone,
  look,
}: BannerCountdownProps) =>
  look === "compact" ? (
    <CompactCountdown seconds={seconds} tone={tone} />
  ) : (
    <BoxedCountdown seconds={seconds} tone={tone} />
  );

const CompactCountdown = ({
  seconds,
  tone,
}: Omit<BannerCountdownProps, "look">) => {
  const colors = COUNTDOWN_TONES[tone];

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      flexShrink={0}
      pl={1.5}
      pr={2}
      py="1px"
      borderRadius={1}
      bgcolor={colors.background}
      role="timer"
      data-testid="banner-countdown"
    >
      <TimerOutlined sx={{ fontSize: 16, color: colors.icon }} />
      <Typography
        variant="body1"
        fontWeight="bold"
        color={colors.digits}
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {compactTime(seconds)}
      </Typography>
    </Stack>
  );
};

const BoxedCountdown = ({
  seconds,
  tone,
}: Omit<BannerCountdownProps, "look">) => {
  const colors = COUNTDOWN_TONES[tone];

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      width="fit-content"
      spacing={1}
      flexShrink={0}
      px={2}
      py={1}
      borderRadius={1}
      bgcolor={colors.background}
      boxShadow={colors.inset ? COUNTDOWN_INSET_SHADOW : undefined}
      role="timer"
      data-testid="banner-countdown"
    >
      {countdownUnits(seconds).map(({ value, unit }, index) => (
        <Fragment key={unit}>
          {index > 0 && (
            <Typography variant="h6" color={colors.colon} aria-hidden>
              :
            </Typography>
          )}
          <Stack alignItems="center">
            <Box
              px={1}
              borderRadius={1}
              bgcolor="banner.countdownDigitBackground"
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                lineHeight={1.3}
                color={colors.digits}
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {twoDigits(value)}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color={colors.label}
              sx={{ fontSize: 11, lineHeight: "13px", opacity: 0.7 }}
            >
              {unit}
            </Typography>
          </Stack>
        </Fragment>
      ))}
    </Stack>
  );
};

/** A promo code in a dashed box, with a button that copies it. */
export const BannerCode = ({ code }: { code: string }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={2}
    pl={3}
    pr={2.5}
    py={2}
    borderRadius={1}
    bgcolor="primary.selected"
    sx={{ border: 1, borderStyle: "dashed", borderColor: "primary.main" }}
    data-testid="banner-code"
  >
    <Typography variant="body1" fontWeight="bold" flex={1} minWidth={0}>
      {code}
    </Typography>
    <CopyToClipboard>
      {({ copy }) => (
        <IconButton
          aria-label="Copy the code"
          onClick={() => copy(code)}
          sx={{ p: 0, color: "primary.main" }}
        >
          <ContentCopyOutlined sx={{ fontSize: 20 }} />
        </IconButton>
      )}
    </CopyToClipboard>
  </Stack>
);

type StackedOfferProps = {
  countdown?: number;
  ctaLabel?: string;
  onCtaClick: () => void;
  variant: BannerVariant;
};

/**
 * The countdown in boxes above a full-width button, as the Slide-in and the
 * Sticky bottom bar on a phone stack them.
 */
export const StackedOffer = ({
  countdown,
  ctaLabel,
  onCtaClick,
  variant,
}: StackedOfferProps) => (
  <>
    {countdown !== undefined && (
      <BannerCountdown
        seconds={countdown}
        tone={BANNER_VARIANTS[variant].iconTone}
        look="boxes"
      />
    )}
    {ctaLabel && (
      <BannerCta
        label={ctaLabel}
        onClick={onCtaClick}
        variant={variant}
        fullWidth
      />
    )}
  </>
);
